import { useRef, useState } from "react";

import { CloudUpload } from "lucide-react";
import clsx from "clsx";

import { ReadFileFn, ReadFileErrorFn } from "@/types/submission";

interface DropZoneProps {
  onReadFile: ReadFileFn;
  onReadFileError: ReadFileErrorFn;
  disabled?: boolean;
}

export function DropZone({ onReadFile, onReadFileError, disabled = false }: DropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Always prevent the default, or the browser opens the dropped file and leaves the page
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(false);

    const fileItems = [...e.dataTransfer.items].filter((i) => i.kind === "file");

    if (fileItems.length !== 1) {
      onReadFileError({ message: "Only one file allowed", log: false });
      return;
    }

    const entry = fileItems[0]!.webkitGetAsEntry();

    if (entry?.isDirectory) {
      onReadFileError({ message: "Folders are not supported", log: false });
      return;
    }

    const file = fileItems[0]!.getAsFile();

    if (!file) {
      onReadFileError({ message: "Could not read dropped item", log: false });
      return;
    }

    onReadFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onReadFile(files[0]!);
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={clsx(
        "flex items-stretch justify-center h-56",
        "border-2 border-dashed border-blue-300 dark:border-blue-800 rounded-xl text-center",
        "transition-all bg-blue-50/50 dark:bg-blue-950/20",
        {
          "border-blue-400 dark:border-blue-500 bg-blue-50/95 dark:bg-blue-950/50 border-solid shadow-md shadow-blue-300/50 dark:shadow-blue-900/50":
            isDragging,
          "opacity-25 cursor-not-allowed grayscale-100": disabled,
          "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50": !disabled,
          "dark:hover:bg-blue-950/50 hover:-translate-y-0.5": !disabled,
          "hover:shadow-md hover:shadow-blue-300/50 dark:hover:shadow-blue-900/50": !disabled,
        },
      )}
    >
      <label
        className={clsx(
          "flex-1 flex flex-col items-center justify-center py-14 px-4",
          "w-full text-lg focus-visible:outline-2",
          "outline-offset-8 rounded-md gap-y-1",
          {
            "cursor-pointer": !disabled,
            "cursor-not-allowed": disabled,
          },
        )}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <input
          className="hidden"
          ref={inputRef}
          type="file"
          accept=".json,.zip"
          onChange={handleInputChange}
          disabled={disabled}
        />
        <CloudUpload className="mb-2 stroke-blue-950 dark:stroke-blue-100" size={48} />
        {isDragging ? (
          "Drop file here"
        ) : (
          <>
            <span className="sm:text-xl font-medium text-blue-950 dark:text-blue-100">
              Click here to select a file
            </span>
            <span className="text-sm text-blue-950/75 dark:text-blue-100/75">
              Or drag and drop your chat log into this area
            </span>
          </>
        )}
      </label>
    </div>
  );
}
